import React, { forwardRef, useState, useCallback, memo, FocusEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';
import PhoneInputWithCountry from 'react-phone-number-input/react-hook-form';
import parsePhoneNumber from 'libphonenumber-js';
import styled from 'styled-components';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import { mediaMSmMax, mediaMXsMax } from 'helpers/breakpoints';
import { CountrySelectWithIcon } from './CountrySelect';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ForwardedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  // eslint-disable-next-line react/no-unused-prop-types
  onChange?: (value: string) => void;
}

const ForwardedInput = memo(
  forwardRef<HTMLInputElement, ForwardedInputProps>((props, ref) => {
    const { onChange: propOnChange, onBlur: propOnBlur, onFocus: propOnFocus, ...restProps } = props;
    const [inFocus, setFocus] = useState(false);

    const onBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
      (e) => {
        if (propOnBlur) propOnBlur(e);
        setFocus(false);
      },
      [propOnBlur]
    );

    const onFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
      (e) => {
        if (propOnFocus) propOnFocus(e);
        setFocus(true);
      },
      [propOnFocus]
    );

    // Create a handler that converts the event to the expected string value.
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (propOnChange) {
          propOnChange(event.target.value);
        }
      },
      [propOnChange]
    );

    return (
      <RelativeWrapper>
        <InputPhoneElement
          ref={ref}
          {...restProps}
          onChange={handleChange} // use the adapted onChange
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={props.disabled}
          className=""
        />
        <Placeholder active={!!props.value || inFocus}>Phone number</Placeholder>
      </RelativeWrapper>
    );
  })
);
ForwardedInput.displayName = 'ForwardedInput';

interface Props {
  className?: string;
  phone?: string;
  name?: string;
  disabled?: boolean;
}

const PhoneInputComponent = ({ name, className, phone, disabled }: Props) => {
  const { errors, control } = useFormContext();
  const finalName = name || 'phone';

  const validatePhone = useCallback((value: string) => {
    const parsedPhone = value ? parsePhoneNumber(value || '') : null;
    return parsedPhone?.isValid() ? true : 'Please enter valid phone number';
  }, []);

  return (
    <Wrapper className={className}>
      <StyledPhone
        defaultCountry="US"
        name={finalName}
        data-testid="phone-number-input"
        control={control}
        defaultValue={phone || ''}
        inputComponent={ForwardedInput}
        rules={{
          required: 'Please enter phone number',
          validate: validatePhone,
        }}
        displayInitialValueAsLocalNumber
        addInternationalOption={false}
        hasError={!!errors?.[finalName]}
        disabled={disabled}
        countrySelectComponent={CountrySelectWithIcon}
      />
      <ErrorMessage error={errors?.[finalName]} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  .PhoneInput {
    /* This is done to stretch the contents of this component. */
    display: flex;
    align-items: center;
  }

  .PhoneInputInput {
    /* The phone number input stretches to fill all empty space */
    flex: 1 1 0% !important;
    /* The phone number input should shrink
  \t   to make room for the extension input */
    min-width: 0;
    width: 100%;
    border: 0;
    padding: 0 1em;
    align-self: stretch;
    border-left: 1px solid #dbdfe5;
    border-radius: 0 10px 10px 0;
    outline: none;
  }

  .PhoneInputCountryIcon {
    width: calc(1em * 3 / 2);
    height: 1em;
  }

  .PhoneInputCountryIcon--square {
    width: 1em;
  }

  .PhoneInputCountryIcon--border {
    /* Removed \`background-color\` because when an \`<img/>\` was still loading
  \t   it would show a dark gray rectangle. */
    /* For some reason the \`<img/>\` is not stretched to 100% width and height
  \t   and sometime there can be seen white pixels of the background at top and bottom. */
    background-color: transparent;
    /* Border is added via \`box-shadow\` because \`border\` interferes with \`width\`/\`height\`. */
    /* For some reason the \`<img/>\` is not stretched to 100% width and height
  \t   and sometime there can be seen white pixels of the background at top and bottom,
  \t   so an additional "inset" border is added. */
  }

  .PhoneInputCountryIconImg {
    max-width: 100%;
    max-height: 100%;
    box-shadow: 1px 1px 8px rgba(0, 0, 0, 0.2);
  }

  .PhoneInputInternationalIconPhone {
    opacity: 0.8;
  }

  .PhoneInputInternationalIconGlobe {
    opacity: 0.65;
  }

  /* Styling native country \`<select/>\`. */

  .PhoneInputCountry {
    position: relative;
    align-self: stretch;
    display: flex;
    align-items: center;
    margin-right: 0em;
    padding: 0.9em 1.5em 0.9em 0.75em;
  }

  .PhoneInputCountrySelect {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    border: 0;
    opacity: 0;
    cursor: pointer;
    border: 0;
    appearance: none;
  }

  .Select-control {
    -webkit-appearance: none;
    -moz-appearance: none;
  }

  /* A "divider" \`<option/>\` for a native \`<select/>\`. */

  .PhoneInputCountrySelectDivider {
    font-size: 1px;
    background: black;
  }

  ${mediaMXsMax} {
    .PhoneInputCountry {
      padding-left: 15px !important;
      padding-right: 10px !important;

      img {
        width: 15px;
      }

      span {
        font-size: 14px;
        line-height: 18px;
        color: #001d58;
      }

      .PhoneInputCountryIcon {
        width: auto;
        height: auto;
      }
    }
  }
`;

const InputPhoneElement = styled.input`
  width: 100%;
  height: 100%;
  padding: 3px 13px 0 13px;
  outline: none;
`;

const RelativeWrapper = styled.div`
  position: relative;
  flex: 1 1 !important;
  margin-left: 15px;
  min-width: 0;
  width: 100%;
  border: 0;
  //padding: 0 1em;
  -webkit-align-self: stretch;
  align-self: stretch;
  border-radius: 0 10px 10px 0;
`;

const Placeholder = styled.div<{ active: boolean }>`
  position: absolute;
  top: 15px;
  left: 13px;
  color: #5e696a;
  pointer-events: none;
  transition: all 200ms ease;

  ${({ active }) =>
    active &&
    `
    font-size: 10px;
    top: 4px;
  `}
`;

const StyledPhone = styled(PhoneInputWithCountry)<{ hasError: boolean }>`
  ${mediaMSmMax} {
    border: 1px solid ${({ hasError }) => (hasError ? '#bc350a' : '#8e9697')};
    border-radius: 6px;
  }

  .PhoneInputCountry {
    border: 1px solid #8e9697;
    border-radius: 6px;
    ${mediaMSmMax} {
      margin-left: 0;
      border: none;
    }
  }

  input {
    border: 1px solid ${({ hasError }) => (hasError ? '#bc350a' : '#8e9697')};
    border-radius: 6px;
    ${mediaMSmMax} {
      margin-left: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border: none;
      border-left: 1px solid ${({ hasError }) => (hasError ? '#bc350a' : '#8e9697')};
    }
  }
`;

export default memo(PhoneInputComponent);
